import { ASSETS } from "../../Public/assets-data";
import "../../Screens/Login-Screen/Login.css";
import { motion } from 'framer-motion';
import MediaComponent from "../Media/media-component";
import NavLink from "../NavLink/navlink-component";

export default function Button({ children, onClick, type = 'button', className = '', icon = null, image = null, disabled = false, fullWidth = false, size, href = null, raw = false, ...rest }) {
    const sizeMap = {
        sm: 'px-2 !py-3 text-xs',
        md: 'px-4 !py-3 text-base',
        lg: 'px-6 !py-3 text-lg',
        xl: 'px-8 !py-6 text-3xl',
    };
    let sizeClass = sizeMap[size] || sizeMap.md;

    const imageElement = image ? (
        <img src={image} alt="" className="w-5 h-5 mx-2 inline-block align-middle" />
    ) : null;

    if (href) {
        return (
            <NavLink
                href={href}
                showUnderline={false}
                preserveStyle
                className={`${raw ? '' : 'button-component'} ${sizeClass} ${fullWidth ? 'w-full' : ''} ${className}`}
                {...rest}
            >
                {imageElement}
                {raw ? children : <span>{children}</span>}
                {icon && <span className="flex items-center">{icon}</span>}
            </NavLink>
        );
    }

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className={`${raw ? '' : 'button-component'} ${sizeClass} ${fullWidth ? 'w-full' : ''} ${className}`}
            {...rest}
        >
            {imageElement}
            {raw ? children : <span>{children}</span>}
            {icon && <span className="flex items-center">{icon}</span>}
        </button>
    );
}

export function ButtonPrimary(props) {
    return <Button {...props} className="button-component" />;
}

export function ButtonSecondary(props) {
    return <Button {...props} className="button-component-secondary" />;
}

export function ButtonWhite(props) {
    return <Button {...props} className="button-component-white" />;
}

export function ButtonBlue(props) {
    return <Button {...props} className="button-component-blue" />;
}

export function ButtonBlack(props) {
    return <Button {...props} className="button-component-black" />;
}

export function ButtonError(props) {
    return <Button {...props} className="button-component-error" />;
}

export function ButtonTransparent(props) {
    return <Button {...props} className="button-component-transparent" />;
}

export function ButtonInverted(props) {
    return <Button {...props} className="button-component-inverted" />;
}

export function ButtonAnimatedWhite({
    children,
    fullWidth = false,
    onClick,
    disabled = false,
    type = 'button',
    className = '',
    ...props
}) {
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className={`
                group relative inline-flex items-center justify-center gap-4
                ${fullWidth ? 'w-full' : 'w-fit'}
                text-primary-900 dark:text-white
                font-bold uppercase tracking-[0.2em]
                text-base lg:text-lg
                py-4 px-0
                transition-all duration-300
                disabled:opacity-50 disabled:cursor-not-allowed
                ${className}
            `}
            {...props}
        >
            <span className="relative z-10 whitespace-nowrap transition-transform duration-300 group-hover:-translate-x-1">
                {children}
            </span>

            <span className="relative z-10 inline-flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full text-secondary-500">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="3"
                    stroke="currentColor"
                    className="absolute h-6 w-6 transition-transform duration-500 ease-out group-hover:-translate-x-[180%]"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
                </svg>

                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="3"
                    stroke="currentColor"
                    className="absolute h-6 w-6 translate-x-[180%] transition-transform duration-500 ease-out group-hover:translate-x-0"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
                </svg>
            </span>
        </button>
    );
}

/**
 * ButtonChevronReveal
 * On hover: label fades out, a right-side panel expands to full width revealing an arrow.
 * Inspired by: 21st.dev/community/components/shsfwork/get-started-button
 *
 * Props: children, fullWidth, size (sm|md|lg|xl), onClick, disabled, loading, className
 */
export function ButtonChevronReveal({ children, fullWidth = false, size = 'lg', onClick, disabled = false, loading = false, className = '', ...rest }) {
    const sizeMap = { sm: 'h-10 px-4 text-xs', md: 'h-12 px-5 text-sm', lg: 'h-14 px-7 text-base', xl: 'h-16 px-9 text-lg' };
    const sz = sizeMap[size] || sizeMap.lg;
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled || loading}
            className={`group relative overflow-hidden inline-flex items-center justify-center bg-primary-950 text-white font-bold uppercase tracking-widest rounded-xl transition-all duration-200 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed ${sz} ${fullWidth ? 'w-full' : ''} ${className}`}
            {...rest}
        >
            {/* Label, fades out on hover */}
            <span className="mr-6 transition-opacity duration-400 group-hover:opacity-0 whitespace-nowrap">
                {loading ? '...' : children}
            </span>
            {/* Expanding arrow panel */}
            <i className="absolute right-1 top-1 bottom-1 rounded-xl z-10 grid w-[2.5rem] place-items-center transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] bg-white/15 group-hover:w-[calc(100%-0.5rem)] not-italic">
                <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
            </i>
        </button>
    );
}

/**
 * ButtonFlowHover
 * On hover: a large circle "flows" in from the bottom-right corner, flooding the button with color.
 * Inspired by: 21st.dev/community/components/vaib215/flow-hover-button
 *
 * Props: children, fullWidth, size (sm|md|lg|xl), onClick, disabled, loading, icon, flowColor (Tailwind bg class), className
 */
export function ButtonFlowHover({ children, fullWidth = false, size = 'lg', onClick, disabled = false, loading = false, icon = null, flowColor = 'before:bg-secondary-500', className = '', ...rest }) {
    const sizeMap = { sm: 'h-10 px-4 text-xs', md: 'h-12 px-5 text-sm', lg: 'h-14 px-7 text-base', xl: 'h-16 px-9 text-lg' };
    const sz = sizeMap[size] || sizeMap.lg;
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled || loading}
            className={[
                'relative z-0 inline-flex items-center justify-center gap-2.5 overflow-hidden',
                'bg-secondary-500 text-white font-bold uppercase tracking-widest rounded-xl',
                'transition-all duration-500',
                // Pseudo-element circle, starts off-screen bottom-right
                'before:absolute before:inset-0 before:-z-10',
                'before:translate-x-[150%] before:translate-y-[150%] before:scale-[2.5]',
                'before:rounded-[100%] before:transition-transform before:duration-[900ms] before:ease-out before:content-[\'\']',
                // 'before:bg-blue-500', // Default flow color, can be overridden by prop
                flowColor,
                // Hover: circle floods in, button scales up slightly
                'hover:scale-[1.02] hover:before:translate-x-[0%] hover:before:translate-y-[0%]',
                'active:scale-[0.97] disabled:bg-secondary-600 disabled:cursor-not-allowed',
                sz,
                fullWidth ? 'w-full' : '',
                className,
            ].filter(Boolean).join(' ')}
            {...rest}
        >
            {icon && <span className="shrink-0">{icon}</span>}
            <span>{loading ? '...' : children}</span>
        </button>
    );
}

export function GeminiButton({ children, onClick, disabled = false, loading = false, className = '' }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled || loading}
            className={`button-gemini ${className}`}
        >

            <span>{loading ? '...' : children}</span>
        </button>
    );
}

export function ButtonCommunity({
    href,
    label,
    sublabel,
    image = ASSETS.ZOHO,
    className = '',
}) {
    return (
        <Button
            href={href}
            raw
            className={`
                group relative overflow-hidden
                inline-flex items-center gap-4
                rounded-full
                bg-white text-primary-950
                px-4 py-3
                shadow-xl shadow-black/20
                transition-all duration-300
                shrink-0
                before:absolute before:inset-0
                before:bg-secondary-500
                before:translate-y-full
                before:transition-transform
                before:duration-500
                hover:before:translate-y-0
                ${className}
            `}
        >
            <span
                className="
                    relative z-10
                    flex h-12 w-12
                    items-center justify-center
                    rounded-xl
                    bg-white
                    border border-black/5
                    shadow-sm
                    shrink-0
                "
            >
                {image ? (
                    <MediaComponent
                    useSekeleton={false}
                        src={image}
                        alt="Zoho"
                        quality={50}
                        className="
                            max-w-10
                            object-contain
                            transition-transform duration-500
                        "
                    />
                ) : (
                    <span className="font-bold text-primary-950 text-sm">{(label || '').trim().charAt(0).toUpperCase()}</span>
                )}
            </span>

            <span className="relative z-10 flex flex-col items-start text-start">
                <span className="font-black text-sm leading-tight whitespace-nowrap transition-colors duration-300 group-hover:text-white">
                    {label}
                </span>

                {sublabel && (
                    <span className="text-primary-500/70 font-bold text-[11px] mt-0.5 whitespace-nowrap transition-colors duration-300 group-hover:text-white/75">
                        {sublabel}
                    </span>
                )}
            </span>

            <span className="relative z-10 flex h-9 w-9 items-center justify-center rounded-full bg-primary-950/5 text-primary-500 transition-all duration-300 group-hover:bg-white/20 group-hover:text-white group-hover:translate-x-1 shrink-0">
                <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.7"
                >
                    <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </span>
        </Button>
    );
}


export function QuantityStepper({ quantity, setQuantity, maxQty }) {
    return (
        <div className="flex items-center border-2 border-gray-100 rounded-2xl bg-gray-50 p-1 shadow-inner shrink-0">
            <button
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                className="w-12 h-12 flex items-center justify-center text-2xl font-light hover:bg-white rounded-2xl transition-all"
            >
                −
            </button>
            <motion.span
                key={quantity}
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.15 }}
                className="w-14 text-center font-bold text-primary-950 text-xl tabular-nums"
            >
                {quantity}
            </motion.span>
            <button
                onClick={() => setQuantity(q => Math.min(maxQty, q + 1))}
                disabled={quantity >= maxQty}
                className="w-12 h-12 flex items-center justify-center text-2xl font-light hover:bg-white rounded-2xl transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
                +
            </button>
        </div>
    );
}

export function ToggleButton({
    isOn = false,
    onToggle = () => { },
    disabled = false,
    required = false,
    className = ''
}) {
    const handleClick = () => {
        if (disabled) return;
        onToggle(!isOn);
    };

    return (
        <button
            dir="ltr"
            type="button"
            role="switch"
            aria-checked={isOn}
            aria-label={isOn ? 'On' : 'Off'}
            onClick={handleClick}
            disabled={disabled}
            className={`relative w-12 h-6 flex items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 ${disabled
                ? 'bg-gray-300 cursor-not-allowed'
                : isOn
                    ? 'bg-green-500'
                    : 'bg-gray-400'
                } ${className}`}
        >
            <input
                type="checkbox"
                checked={isOn}
                onChange={() => { }}
                required={required}
                tabIndex={-1}
                className="absolute w-px h-px -m-px overflow-hidden whitespace-nowrap border-0 p-0"
            />
            <span
                className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-300 ${isOn ? 'translate-x-6' : 'translate-x-1'
                    }`}
            />
        </button>
    );
}